import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { MagnifyingGlass, X, Link as LinkIcon } from "@phosphor-icons/react";

/**
 * LinkedRecordPicker — searchable picker for cross-module records that a
 * task is linked to. The list of available modules + how each module is
 * queried is described in MODULE_CONFIG so the picker stays small and
 * declarative.
 *
 * Props:
 *   module    — one of: project, asset, finance, procurement, compliance, hr
 *   value     — currently-selected record id
 *   label     — display label for the chip
 *   onPick    — (rec | null) => void  — receives the picked record (with its
 *               full payload) or null when cleared. The TaskForm uses this
 *               payload to auto-fill title / due_date / assignee.
 *   disabled
 */
const MODULE_CONFIG = {
  asset: {
    endpoint: (q) => `/assets/lookup?q=${encodeURIComponent(q)}&limit=15`,
    label: "Asset",
    placeholder: "Search by asset code, name, tag…",
    titleField: "description",
    subtitleFields: ["asset_code", "category", "location"],
    titlePrefix: (r) => r.asset_code ? `[${r.asset_code}] ` : "",
  },
  finance: {
    endpoint: (q) => `/finance/entries?q=${encodeURIComponent(q)}&limit=15`,
    label: "Finance entry",
    placeholder: "Search by description, category, vendor…",
    titleField: "description",
    subtitleFields: ["entry_type", "category", "amount"],
    titlePrefix: (r) => r.category ? `[${r.category}] ` : "",
  },
  procurement: {
    endpoint: (q) => `/procurement?q=${encodeURIComponent(q)}&limit=15`,
    label: "Procurement",
    placeholder: "Search by RFQ/PO, vendor, item…",
    titleField: "title",
    subtitleFields: ["po_number", "vendor_name", "status"],
    titlePrefix: (r) => r.po_number ? `[${r.po_number}] ` : "",
  },
  compliance: {
    endpoint: (q) => `/compliance?q=${encodeURIComponent(q)}&limit=15`,
    label: "Compliance",
    placeholder: "Search by name, type, authority…",
    titleField: "name",
    subtitleFields: ["compliance_type", "authority", "status"],
    titlePrefix: () => "",
  },
  hr: {
    endpoint: (q) => `/hr/staff?q=${encodeURIComponent(q)}&limit=15`,
    label: "Staff record",
    placeholder: "Search by name, staff no., department…",
    titleField: "full_name",
    subtitleFields: ["employee_code", "designation", "department"],
    titlePrefix: () => "",
  },
};

export default function LinkedRecordPicker({ module, value, label, onPick, disabled = false }) {
  const cfg = MODULE_CONFIG[module];
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!cfg) return;
    if (!q || q.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(cfg.endpoint(q));
        const items = data.items || (Array.isArray(data) ? data : []);
        setResults(items.slice(0, 15));
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 220);
    return () => clearTimeout(t);
  }, [q, cfg]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  if (!cfg) return null;

  return (
    <div ref={ref} className="relative">
      {value && (
        <div className="flex items-start gap-2 rounded-md border border-blue-300 bg-blue-50/60 p-2 text-sm" data-testid={`linked-${module}-selected`}>
          <LinkIcon size={14} className="mt-0.5 flex-shrink-0 text-blue-700" />
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-slate-900 text-sm truncate">{label}</div>
            <div className="font-mono-alt text-[10px] uppercase tracking-wider text-slate-500">{cfg.label} · linked</div>
          </div>
          {!disabled && (
            <button
              type="button"
              onClick={() => { onPick(null); setQ(""); }}
              className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-rose-600 hover:bg-rose-100"
              data-testid={`linked-${module}-clear`}
              title="Clear linked record"
            >
              <X size={11} weight="bold" />
            </button>
          )}
        </div>
      )}
      {!value && (
        <div className="relative">
          <MagnifyingGlass size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => { setQ(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder={cfg.placeholder}
            disabled={disabled}
            className="pl-8"
            data-testid={`linked-${module}-input`}
          />
          {open && q.length >= 2 && (
            <div className="absolute z-50 mt-1 max-h-72 w-full overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg" data-testid={`linked-${module}-dropdown`}>
              {loading && <div className="px-3 py-2 text-xs text-slate-500">Searching…</div>}
              {!loading && results.length === 0 && (
                <div className="px-3 py-2 text-xs text-slate-500">No matching {cfg.label.toLowerCase()} found</div>
              )}
              {results.map((r) => {
                const title = `${cfg.titlePrefix(r)}${r[cfg.titleField] || r.title || r.name || "Untitled"}`;
                const sub = cfg.subtitleFields.map((f) => r[f]).filter(Boolean).join(" · ");
                return (
                  <button
                    key={r.id || r._id}
                    type="button"
                    onClick={() => { onPick({ ...r, _display_title: title }); setQ(""); setOpen(false); }}
                    className="block w-full px-3 py-2 text-left text-sm hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
                    data-testid={`linked-${module}-opt-${r.id || r._id}`}
                  >
                    <div className="font-semibold text-slate-900 truncate">{title}</div>
                    {sub && <div className="text-[11px] text-slate-500 truncate">{sub}</div>}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
